import {StandardSchemaV1} from '@standard-schema/spec';
import BigNumber from 'bignumber.js';
import {NonEmptyReadonlyArray} from 'effect/Array';
import {constant, dual} from 'effect/Function';
import {not, or, Predicate, Refinement} from 'effect/Predicate';
import * as internal from './internal/Wish.internal';

/**
 * Represents a reusable validation or shaping routine that inspects a value and returns a Wish result.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const PositiveNumber = Wish.number.pipe(Wish.refine(n => n > 0, 'must be positive'));
 * const outcome = PositiveNumber.call(42);
 *
 * if (Wish.isOk(outcome)) {
 *   console.log(outcome.output); // 42
 * }
 * ```
 */
export type Wish<T> = internal.Wish<T>;

export type Entry<T, O extends boolean> = internal.WishEntry<T, O>;
export const makeEntry = internal.WishEntry.make;

/**
 * Extracts the output type produced by a {@link Wish}.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const UserWish = Wish.struct({name: Wish.string, age: Wish.number});
 * type User = Wish.Infer<typeof UserWish>;
 * //    ^? { name: string; age: number }
 * ```
 */
export type Infer<T extends Wish<any>> = internal.Infer<T>;

/**
 * Builds a {@link Wish} from a custom evaluator function.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const NonEmptyString = Wish.make<string>(function (value: unknown) {
 *   return typeof value === 'string' && value.trim().length > 0
 *     ? Wish.makeOk(value)
 *     : Wish.makeFail('must be a non-empty string');
 * });
 *
 * const result = NonEmptyString.call('hello');
 * ```
 */
export const make = internal.Wish.make;

export const run: {
    <T>(thing: unknown, wish: Wish<T>): internal.WishResult<T>;
    <T>(wish: Wish<T>): {
        (thing: unknown): internal.WishResult<T>;
    };
} = dual(2, <T>(thing: unknown, wish: Wish<T>): internal.WishResult<T> => {
    return wish.call(thing);
});

export const assert = <T>(wish: Wish<T>, thing: unknown): T => {
    const result = wish.call(thing);
    if (isFail(result) || isCope(result)) {
        throw new Error(result.issues.map(i => [i.at.join('.'), i.message].join(' ')).join('\n'));
    }
    return result.output;
};

/**
 * Parses the wish and returns true if it went Ok. It CANNOT be a type predicate because Wish can
 * be a morph from unknown to T and if we say that canParse returns `thing` is T it will be incorrect
 * because `thing` can be of unkonwn input type and not T, but CAN BE PARSED into T.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const canHaveAccountId = Wish.canParse(
 *     Wish.struct({ accountId: Wish.string.pipe(Wish(n => parseInt(n) as AccountId)) })
 * );
 *
 * if (canHaveAccountId(params)) {
 *   console.lot(params) // { accountId: "123" } // not { accountId: 123 }
 * }
 * ```
 */
export const canParse =
    (wish: Wish<unknown>) =>
    (thing: unknown): boolean =>
        isOk(wish.call(thing));

/**
 * Type guard that checks whether the provided value is any Wish result.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const result = Wish.number.call(123);
 *
 * if (Wish.isResult(result)) {
 *   console.log(result._tag);
 * }
 * ```
 */
export const isResult = internal.WishResult.is;

/**
 * Type guard for successful Wish results.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const result = Wish.number.call(123);
 *
 * if (Wish.isOk(result)) {
 *   console.log(result.output); // 123
 * }
 * ```
 */
export const isOk = internal.WishOk.is;

/**
 * Constructs a Wish result representing a successful evaluation.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const ok = Wish.makeOk('value');
 * console.log(ok._tag); // "Ok"
 * ```
 */
export const makeOk = internal.WishOk.make;

/**
 * Type guard for Wish results produced through cope recovery.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const recoverNumber = Wish.number.pipe(Wish.cope(Wish.string, Number));
 * const result = recoverNumber.call('42');
 *
 * if (Wish.isCope(result)) {
 *   console.log(result.output); // 42
 * }
 * ```
 */
export const isCope = internal.WishCope.is;

/**
 * Constructs a Wish result that signals recovery with a fallback output.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const cope = Wish.makeCope([{message: 'initial failure'}], 'fallback');
 * console.log(cope._tag); // "Cope"
 * ```
 */
export const makeCope = internal.WishCope.make;

/**
 * Type guard for Wish failures that contain collected issues.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const result = Wish.number.call('not-a-number');
 *
 * if (Wish.isFail(result)) {
 *   console.error(result.issues);
 * }
 * ```
 */
export const isFail = internal.WishFail.is;

/**
 * Constructs a Wish failure with a human-readable message.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const fail = Wish.makeFail('something went wrong');
 * console.log(fail._tag); // "Fail"
 * ```
 */
export const makeFail = internal.WishFail.make;

export const isNotOk = or(isFail, isCope);

export const hasOut = or(isOk, isCope);

export const mapOutput = internal.WishResult.mapOutput;

/**
 * Enhances a Wish with a fallback Wish and recovery logic that re-runs the primary Wish with a transformed value.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const IntegerFromString = Wish.number.pipe(
 *   Wish.cope(Wish.string, value => Number.parseInt(value, 10))
 * );
 *
 * const recovered = IntegerFromString.call('42');
 * Wish.isCope(recovered); // true
 * ```
 */
export const cope =
    <B, A = never>(secondaryWish: Wish<B>, fallbackLogic: (value: B) => NoInfer<A>) =>
    (primaryWish: Wish<A>): Wish<A> =>
        make<A>(function wishCope(this: unknown, thing: unknown = this) {
            const primaryOut = primaryWish.call(thing);
            if (isOk(primaryOut)) return primaryOut;

            const secondOut = secondaryWish.call(thing);
            if (isFail(secondOut)) return secondOut;

            const copeResult = primaryWish.call(fallbackLogic(secondOut.output));
            if (isOk(copeResult)) {
                return makeCope(primaryOut.issues, copeResult.output);
            } else {
                return copeResult;
            }
        });

/**
 * Extends a Wish so that a fallback Wish can produce an alternative output when the primary Wish fails.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const NumberOrFallback = Wish.number.pipe(
 *   Wish.copeTo(Wish.string, value => value.length)
 * );
 *
 * const result = NumberOrFallback.call('abc');
 * Wish.isCope(result); // true
 * console.log(result.output); // 3
 * ```
 */
export const copeTo =
    <A, B, C = A>(secondaryWish: Wish<B>, fallbackLogic: (value: B) => C) =>
    (primaryWish: Wish<A>): Wish<A | C> =>
        make<A | C>(function wishCopeTo(this: unknown, thing: unknown = this) {
            const primaryOut = primaryWish.call(thing);
            if (isOk(primaryOut)) return primaryOut;

            const secondOut = secondaryWish.call(thing);
            if (isFail(secondOut)) return secondOut;

            return makeCope(primaryOut.issues, fallbackLogic(secondOut.output));
        });

/**
 * Adapts a Wish into a Standard Schema V1 compatible validator.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const Schema = Wish.toStandardSchemaV1(Wish.number);
 * Schema['~standard'].validate(42); // => { value: 42 }
 * ```
 */
export const toStandardSchemaV1 = <T>(
    wish: Wish<T>,
    options: internal.WishResult.StandardSchemaV1Options = {}
): StandardSchemaV1<unknown, T> => {
    return {
        '~standard': {
            vendor: 'wish',
            version: 1,
            validate(value) {
                return internal.WishResult.toStandardResult(wish.call(value), options);
            },
        },
    };
};

/**
 * Narrows the output of a Wish using a refinement predicate.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const PositiveNumber = Wish.number.pipe(Wish.refine((n): n is number => n > 0, 'must be positive'));
 * const result = PositiveNumber.call(-1);
 * Wish.isFail(result); // true
 * ```
 */
export const refine: {
    <A, B extends A = A>(
        refinement: Refinement<NoInfer<A>, B>,
        message?: string
    ): {
        (wish: Wish<A>): Wish<B>;
    };
    <A>(
        refinement: Predicate<NoInfer<A>>,
        message?: string
    ): {
        (wish: Wish<A>): Wish<A>;
    };
    <A, B extends A = A>(wish: Wish<A>, refinement: Refinement<NoInfer<A>, B>, message?: string): Wish<B>;
    <A>(wish: Wish<A>, refinement: Predicate<NoInfer<A>>, message?: string): Wish<A>;
} = dual(
    3,
    <A, B extends A = A>(
        wish: Wish<A>,
        refinement: Predicate<A> | Refinement<A, B>,
        message = "didn't pass refinement"
    ) =>
        make<B>(function wishRefine(this: unknown, thing: unknown = this): internal.WishResult<B> {
            const result = wish.call(thing);
            if (isFail(result)) return result;
            if (refinement(result.output)) {
                return result as internal.WishOk<B> | internal.WishCope<B>;
            } else {
                return makeFail(message);
            }
        })
);

/**
 * Wish that ensures the input is a number and not a NaN.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * Wish.number.call(123);
 * ```
 */
export const number = internal.makePrimitive('number').pipe(refine(not(Number.isNaN), 'is NaN'));

/**
 * Wish that ensures the input is a boolean.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * Wish.boolean.call(true);
 * ```
 */
export const boolean = internal.makePrimitive('boolean');

/**
 * Wish that ensures the input is a string.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * Wish.string.call('hello');
 * ```
 */
export const string = internal.makePrimitive('string');

/**
 * Wish that ensures the input is a bigint.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * Wish.bigint.call(10n);
 * ```
 */
export const bigint = internal.makePrimitive('bigint');

/**
 * Wish that ensures the input is a symbol.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * Wish.symbol.call(Symbol('id'));
 * ```
 */
export const symbol = internal.makePrimitive('symbol');

/**
 * Creates a Wish that validates an array whose elements satisfy the provided Wish.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const StringArray = Wish.array(Wish.string);
 * StringArray.call(['a', 'b']);
 * ```
 */
export const array = internal.makeWishArray;

export const record = internal.makeWishRecord;

/**
 * Creates a Wish that validates objects matching the provided field definition map.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const User = Wish.struct({ name: Wish.string, age: Wish.number });
 * User.call({ name: 'Ada', age: 37 });
 * ```
 */
export const struct = internal.makeWishStruct;

export type StructDef = internal.StructDef;
export type StructOut<P extends StructDef> = internal.StructOut<P>;

/**
 * Creates a Wish that accepts one of the given Wishes.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const StringOrNumber = Wish.choice(Wish.string, Wish.number);
 * StringOrNumber.call(42);
 * ```
 */
export const choice = internal.makeWishChoice;

/**
 * Wish that always fails, useful as a sentinel value.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * Wish.Never.call('anything'); // => Fail
 * ```
 */
export const Never = make<never>(function () {
    return makeFail('is never');
});

export const Unknown = make<unknown>(function (this: unknown, thing: unknown = this) {
    return makeOk(thing);
});

/**
 * Wish that always succeeds with an undefined output, discarding original value.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * Wish.Void.call('anything');
 * ```
 */
export const Void = make<void>(function () {
    return makeOk(undefined);
});

/**
 * Creates a Wish that accepts one of the provided literal values.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const Status = Wish.literal('pending', 'done');
 * Status.call('pending');
 * ```
 */
export const literal = <TLiterals extends NonEmptyReadonlyArray<internal.primitive>>(...literals: TLiterals) => {
    type T = TLiterals[number];
    if (!literals.length) {
        return Never;
    }
    const displayText = literals.map(l => JSON.stringify(l)).join('|');
    return make<T>(function wishLiteral(this: unknown, thing: unknown = this) {
        for (let i = 0; i < literals.length; i++) {
            if (literals[i] === thing) {
                return makeOk(thing as T);
            }
        }
        return makeFail(`is not \`${displayText}\``);
    });
};

/**
 * Remaps the output of a Wish entry to a different key when used inside {@link struct}.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const User = Wish.struct({
 *   username: Wish.string.pipe(Wish.at('name')),
 * });
 *
 * User.call({ username: 'Ada' }).output.name; // "Ada"
 * ```
 */
export const at: {
    (fieldKey: internal.Key): {
        <T>(wish: internal.Wish.Callable<T>): internal.WishEntry<T, false>;
    };
    <T>(wish: internal.Wish.Callable<T>, fieldKey: internal.Key): internal.WishEntry<T, false>;
} = dual(2, <T>(wish: internal.Wish.Callable<T>, fieldKey: internal.Key) =>
    internal.WishEntry.makeForKey(fieldKey, wish)
);

/**
 * Marks a struct entry as optional, allowing the key to be omitted.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const User = Wish.struct({
 *   name: Wish.string,
 *   nickname: Wish.string.pipe(Wish.optional),
 * });
 *
 * User.call({ name: 'Ada' });
 * ```
 */
export const optional = internal.WishEntry.makeOptional;

export const nullable = <T>(wish: internal.WishLike<T>) => choice(wish, literal(null));
export const orNull = nullable;
export const nullOr = orNull;

export const orUndefined = <T>(wish: Wish<T>) => choice(wish, literal(undefined));
export const undefinedOr = orUndefined;

/**
 * Transforms the successful output of a Wish with the provided mapping function.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const UpperName = Wish.string.pipe(Wish.morph(name => name.toUpperCase()));
 * const result = UpperName.call('ada');
 *
 * if (Wish.isOk(result)) {
 *   console.log(result.output); // ADA
 * }
 * ```
 */
export const morph =
    <T, U>(morph: (value: T) => internal.WishResult<U> | U) =>
    (wish: Wish<T>) =>
        make<U>(function wishMorph(this: unknown, thing: unknown = this) {
            const result = wish.call(thing);
            if (isFail(result)) {
                return result;
            }
            try {
                const morphResult = morph(result.output);
                if (!isResult(morphResult)) {
                    return {
                        ...internal.WishResult.coerce<U>()(result),
                        output: morphResult,
                    };
                }
                if (isFail(morphResult)) {
                    return morphResult;
                }
                return {
                    ...internal.WishResult.coerce<U>()(result),
                    output: morphResult.output,
                };
            } catch (error) {
                return makeFail(`morph failed: ${error}`);
            }
        });
/**
 * Transforms only cope results, leaving ok and fail outcomes untouched.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const RecoverToUpper = Wish.string.pipe(
 *   Wish.cope(Wish.number, n => String(n))
 * ).pipe(
 *   Wish.morphIfCope(value => value.toUpperCase())
 * );
 * ```
 */
export const morphIfCope =
    <T, U>(morph: (value: T) => U) =>
    (wish: Wish<T>) =>
        make<U | T>(function wishMorphIfCope(this: unknown, thing: unknown = this) {
            const result = wish.call(thing);
            if (isOk(result) || isFail(result)) {
                return result;
            }
            return makeCope(result.issues, morph(result.output));
        });

/**
 * Filters the array output of a Wish according to a predicate, preserving coping behavior.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const EvenNumbers = Wish.array(Wish.number).pipe(
 *   Wish.filter((value): value is number => value % 2 === 0)
 * );
 *
 * EvenNumbers.call([1, 2, 3, 4]);
 * ```
 */
export function filter<U>(
    filterFn: (value: unknown) => value is U
): <T>(wish: Wish<ReadonlyArray<T>>) => Wish<ReadonlyArray<Extract<T, U>>>;

export function filter<T, U extends T>(
    filterFn: (value: T) => value is U
): (wish: Wish<ReadonlyArray<T>>) => Wish<Array<Extract<T, U>>>;

export function filter<T>(filterFn: (value: T) => boolean): (wish: Wish<ReadonlyArray<T>>) => Wish<ReadonlyArray<T>>;

export function filter<T>(filterFn: (value: T) => boolean): any {
    return (wish: Wish<ReadonlyArray<T>>) => wish.pipe(morph(arr => arr.filter(elem => filterFn(elem))));
}

/**
 * Rejects elements from the array output of a Wish according to a predicate, preserving coping behavior.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const WithoutNegatives = Wish.array(Wish.number).pipe(
 *   Wish.reject(value => value < 0)
 * );
 *
 * WithoutNegatives.call([-1, 0, 5]);
 * ```
 */
export function reject<U>(
    rejectFn: (value: unknown) => value is U
): <T>(wish: Wish<ReadonlyArray<T>>) => Wish<ReadonlyArray<Exclude<T, U>>>;

export function reject<T, U extends T>(
    rejectFn: (value: T) => value is U
): (wish: Wish<ReadonlyArray<T>>) => Wish<ReadonlyArray<Exclude<T, U>>>;

export function reject<T>(rejectFn: (value: T) => boolean): (wish: Wish<ReadonlyArray<T>>) => Wish<ReadonlyArray<T>>;

export function reject<T>(rejectFn: (value: T) => boolean): any {
    return (wish: Wish<ReadonlyArray<T>>) => wish.pipe(morph(arr => arr.filter(elem => !rejectFn(elem))));
}

const SkipToken = Symbol.for('wish/SkipToken');

/**
 * Converts cope recoveries into a skip token that can later be filtered out.
 * Only works if Wish alread has coping mechanism.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const OptionalNumber = Wish.string.pipe(
 *   Wish.copeTo(Wish.literal(''), () => undefined)
 * ).pipe(Wish.copeToSkip);
 * ```
 */
export const copeToSkip = <T>(wish: Wish<T>) => wish.pipe(morphIfCope(constant(SkipToken)));

/**
 * Helper that removes skip tokens produced by {@link copeToSkip} from array outputs.
 *
 * @example
 * ```ts
 * import * as Wish from './Wish';
 *
 * const CleanArray = Wish.array(Wish.string.pipe(Wish.copeToSkip)).pipe(Wish.rejectSkip);
 * CleanArray.call(['a', 'b']);
 * ```
 */
export const rejectSkip = <T>(wish: Wish<ReadonlyArray<T>>) => wish.pipe(reject(element => element === SkipToken));

/**
 *
 */
export const BigNumberFromString = string.pipe(
    refine(RegExp.prototype.test.bind(/^-?\d+$|^-?\d+\.\d+$/), 'is not numeric string'),
    morph(n => BigNumber(n))
);

/**
 *
 */
export const DateFromString = string.pipe(morph(d => new Date(d)));
